import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("inv_token", data.token);
    setUser({
      id: data.id, email: data.email, name: data.name, role: data.role,
      roles: data.roles, capabilities: data.capabilities || [],
      must_change_password: data.must_change_password,
    });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error(e); }
    localStorage.removeItem("inv_token");
    setUser(false);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) { console.error(e); }
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const ROLES = {
  SUPER_ADMIN: "super_admin",
  INVENTORY: "inventory",
  SALES: "sales",
};

// Get the user's roles list (handles legacy single `role` field too).
export const userRoles = (user) => {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length) return user.roles;
  if (user.role) return [user.role];
  return [];
};
export const hasRole = (user, role) => userRoles(user).includes(role);

// Fine-grained capabilities (additive to roles). Super-admins always pass.
export const userCapabilities = (user) => (user && Array.isArray(user.capabilities)) ? user.capabilities : [];
export const hasCapability = (user, cap) => {
  if (!user) return false;
  if (hasRole(user, "super_admin")) return true;
  return userCapabilities(user).includes(cap);
};

// All capabilities the UI knows about. Adding a new key here makes it appear in
// the Users editor automatically.
export const ALL_CAPABILITIES = [
  { key: "add_expenses", label: "Agregar gastos", description: "Permite registrar nuevos gastos. No verá listados ni dashboard financiero." },
  { key: "crm_supervisor", label: "Supervisor de CRM", description: "Puede ver y modificar las tarjetas y clientes de TODOS los vendedores en el CRM." },
  { key: "sales_supervisor", label: "Supervisor de Notas de Venta", description: "Puede ver, editar y modificar las notas de venta de TODOS los vendedores (con justificación, igual que un super_admin). También incluye acceso al CRM y clientes de todos los vendedores." },
  { key: "manage_online_orders", label: "Gestor de tienda online", description: "Acceso al panel de pedidos generados desde la tienda pública. Puede marcarlos como preparados, enviados o entregados." },
  { key: "suggest_photo_changes", label: "Sugerir cambios de fotos", description: "Puede revisar las fotos de los productos en la tienda y enviar sugerencias al equipo de Marketing cuando alguna no cumpla con el estándar." },
  { key: "manage_price_research", label: "Price Research", description: "Acceso al módulo APC Price Research: monitorear precios de mercado, ejecutar investigaciones y revisar recomendaciones." },
  { key: "use_alendra_ai", label: "Alendra AI", description: "Puede usar el asistente IA por producto: generar descripciones, publicaciones y chatear con la IA para apoyarse en sus ventas." },
  { key: "manage_preinventory", label: "Preinventario", description: "Facultad de inventario: puede dar de alta equipos del Hospital Interno a Preinventario, completar fichas (costos, precios, fotos, tienda) y enviarlas a Inventario." },
];

const ROLE_AREAS = {
  super_admin: ["dashboard", "products", "movements", "reports", "settings", "users", "clients", "sales", "totals", "banks", "finance", "expenses", "expenses_add", "portal_orders", "marketing", "publications", "publications_overview", "online_orders", "marketing_admin", "price_research", "alendra_ai", "hr", "inventory", "preinventory"],
  inventory:   ["dashboard", "products", "movements", "settings", "inventory", "preinventory"],
  sales:       ["dashboard", "products_readonly", "clients", "sales", "portal_orders", "publications"],
  marketing:   ["marketing"],
  technician:  ["dashboard", "inventory"],
  taller:      ["dashboard", "inventory"],
};

// Map of fine-grained capability → UI areas unlocked when present.
const CAP_AREAS = {
  add_expenses: ["expenses_add"],
  manage_online_orders: ["online_orders"],
  manage_price_research: ["price_research"],
  use_alendra_ai: ["alendra_ai"],
  manage_preinventory: ["preinventory"],
};

// Allowed if ANY of the user's roles permits this area, OR any capability unlocks it.
export const can = (userOrRole, area) => {
  if (typeof userOrRole === "string") {
    return (ROLE_AREAS[userOrRole] || []).includes(area);
  }
  if (!userOrRole) return false;
  if (userRoles(userOrRole).some((r) => (ROLE_AREAS[r] || []).includes(area))) return true;
  return userCapabilities(userOrRole).some((c) => (CAP_AREAS[c] || []).includes(area));
};
